import { useState, useEffect, useRef } from 'react';
import './style.css';
import { useParams, useLocation, useNavigate } from "react-router-dom";
import stageLogo from '../../assets/images/logo-brand-w.svg';
import ssoLogo from '../../assets/images/lockIcon.svg';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { ToastContainer, toast } from "react-toastify"
import 'react-toastify/dist/ReactToastify.css';
import FullScreenLoader from '../../Component/Loader';
import { useDispatch } from "react-redux";
import { getUserInfo, getUserRole } from "../../Services/auth";
import { setAuth } from "../../Redux/Slice/authSlice";

function Login() {
    const { deal_id: routeDealId, countryId: routeCountryId } = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Use refs to track if navigation has already occurred
    const hasNavigatedRef = useRef(false);
    const [showLoader, setShowLoader] = useState(false);

    // ✅ ALWAYS resolve dealId and countryId from best source
    const queryParams = new URLSearchParams(location.search);
    const queryDealId = queryParams.get("deal_id");
    const queryCountryId = queryParams.get("country_id");

    const finalDealId = queryDealId || (!routeDealId || routeDealId === 'undefined' ? localStorage.getItem("dealId") : routeDealId);
    const finalCountryId = queryCountryId || (!routeCountryId || routeCountryId === 'undefined' ? localStorage.getItem("countryId") : routeCountryId);

    // 🔐 Persist dealId and countryId if they come from URL params
    useEffect(() => {
        if (finalDealId && finalDealId !== 'undefined') {
            localStorage.setItem("dealId", finalDealId);
            console.log("Saved dealId to localStorage:", finalDealId);
        }
        if (finalCountryId && finalCountryId !== 'undefined') {
            localStorage.setItem("countryId", finalCountryId);
            console.log("Saved countryId to localStorage:", finalCountryId);
        }
    }, [finalDealId, finalCountryId]);

    // Helper function to validate if both parameters are present
    const hasValidDealAndCountry = (dealId, countryId) => {
        return dealId && dealId !== "undefined" && countryId && countryId !== "undefined";
    };

    // 🔑 Handle SSO callback
    useEffect(() => {
        // Prevent multiple navigations
        if (hasNavigatedRef.current) {
            console.log("Already navigated, skipping...");
            return;
        }

        const accessToken = queryParams.get("access_token");
        const stateParam = queryParams.get("state");

        console.log("SSO Callback - Access Token:", !!accessToken);
        console.log("SSO Callback - State Param:", stateParam);

        // Try to decode state parameter if it exists
        let stateDealId = null;
        let stateCountryId = null;
        if (stateParam) {
            try {
                const decodedState = JSON.parse(atob(stateParam));
                stateDealId = decodedState.dealId;
                stateCountryId = decodedState.countryId;
                console.log("Decoded state:", decodedState);
            } catch (error) {
                console.error("Failed to decode state parameter:", error);
            }
        }

        // Determine the effective values (priority: state > query params > localStorage)
        let effectiveDealId = stateDealId || queryDealId || finalDealId;
        let effectiveCountryId = stateCountryId || queryCountryId || finalCountryId;

        // Fallback to sessionStorage if still undefined
        if (!effectiveDealId || effectiveDealId === 'undefined') {
            effectiveDealId = sessionStorage.getItem('ssoDealId');
            console.log("Falling back to sessionStorage dealId:", effectiveDealId);
        }

        if (!effectiveCountryId || effectiveCountryId === 'undefined') {
            effectiveCountryId = sessionStorage.getItem('ssoCountryId');
            console.log("Falling back to sessionStorage countryId:", effectiveCountryId);
        }

        if (accessToken) {
            console.log("Processing SSO login...");
            getUserData(accessToken, effectiveDealId, effectiveCountryId);
        } else {
            console.log("No access token found in URL");
        }
    }, [location.search]);

    const getUserData = async (token, dealId, countryId) => {
        // Prevent multiple calls
        if (hasNavigatedRef.current) {
            console.log("getUserData already called, skipping...");
            return;
        }

        setShowLoader(true);
        try {
            let data = await getUserInfo(token);
            if (data?.data) {
                // Use the parameters passed to the function
                const effectiveDealId = dealId || finalDealId;
                const effectiveCountryId = countryId || finalCountryId;
                await fetchUserRole(data?.data?.Email, token)

                // Check if we have both parameters valid
                const shouldRedirectToHome = hasValidDealAndCountry(effectiveDealId, effectiveCountryId);

                // Determine redirect path based on parameter validity
                let redirectPath;
                if (shouldRedirectToHome) {
                    redirectPath = `/home/${effectiveDealId}/${effectiveCountryId}`;
                    console.log("Both parameters valid, redirecting to:", redirectPath);
                } else {
                    redirectPath = `/dashboard`;
                    console.log("Missing parameters, redirecting to:", redirectPath);
                }

                // Mark as navigated
                hasNavigatedRef.current = true;

                // Force a small delay to ensure state is updated
                setTimeout(() => {
                    // Store in localStorage for consistency
                    if (effectiveDealId && effectiveDealId !== 'undefined') {
                        localStorage.setItem("dealId", effectiveDealId);
                    }
                    if (effectiveCountryId && effectiveCountryId !== 'undefined') {
                        localStorage.setItem("countryId", effectiveCountryId);
                    }

                    // Navigate to appropriate path
                    navigate(redirectPath, { replace: true });

                    // Set auth state
                    dispatch(setAuth({
                        isAuthenticated: true,
                        token,
                        loginType: 'sso',
                        dealId: shouldRedirectToHome ? effectiveDealId : null,
                        countryId: shouldRedirectToHome ? effectiveCountryId : null
                    }));

                    localStorage.setItem('access_token', token);
                    localStorage.setItem('firstName', data.data.FirstName);
                    localStorage.setItem('lastName', data.data.LastName);
                    localStorage.setItem('userName', data.data.Username);
                    localStorage.setItem('email', data.data.Email);
                    localStorage.setItem('UserId', data.data.UserId);
                    localStorage.setItem('isLoggedIn', 'true');
                    localStorage.setItem('loginType', 'sso');
                    localStorage.setItem('role', JSON.stringify(data.data.Role));

                    // Clear sessionStorage backup
                    sessionStorage.removeItem('ssoDealId');
                    sessionStorage.removeItem('ssoCountryId');

                    console.log("Navigation completed to:", redirectPath);
                }, 100);
            } else {
                toast.error("Failed to get user information");
            }
        } catch (error) {
            console.error("Error in getUserData:", error);
            toast.error("Failed to authenticate. Please try again.");
            hasNavigatedRef.current = false; // Reset on error
            setShowLoader(false);
        } finally {
            setShowLoader(false);
        }
    };

    const handleSsoClick = (e) => {
        e.preventDefault();

        // Check if we have both parameters
        const hasBothParams = hasValidDealAndCountry(finalDealId, finalCountryId);

        if (!hasBothParams) {
            console.warn("Missing one or both parameters, but allowing SSO for dashboard redirect");
            console.log("Parameters:", { finalDealId, finalCountryId });
        } else {
            console.log("SSO Redirect with valid parameters:", { finalDealId, finalCountryId });
        }

        // Store in sessionStorage before redirecting (as backup)
        if (finalDealId && finalDealId !== 'undefined') {
            sessionStorage.setItem('ssoDealId', finalDealId);
        }
        if (finalCountryId && finalCountryId !== 'undefined') {
            sessionStorage.setItem('ssoCountryId', finalCountryId);
        }

        // Also store in localStorage as additional backup
        if (finalDealId && finalDealId !== 'undefined') {
            localStorage.setItem("dealId", finalDealId);
        }
        if (finalCountryId && finalCountryId !== 'undefined') {
            localStorage.setItem("countryId", finalCountryId);
        }

        // Determine the base URL based on environment
        let baseUrl;
        const currentUrl = window.location.href;

        if (currentUrl.includes(process.env.REACT_APP_PROD_URL)) {
            baseUrl = process.env.REACT_APP_PROD_SSO_REDIRECTION_URL;
        } else if (currentUrl.includes(process.env.REACT_APP_LOCAL_URL)) {
            baseUrl = process.env.REACT_APP__LOCAL__SSO_REDIRECTION_URL;
        } else {
            baseUrl = process.env.REACT_APP_SSO_REDIRECTION_URL;
        }
        // Construct the redirect URL with query parameters
        const redirectUrl = new URL(baseUrl);

        // Only add parameters if they're valid
        if (hasValidDealAndCountry(finalDealId, finalCountryId)) {
            const state = btoa(JSON.stringify({
                dealId: finalDealId,
                countryId: finalCountryId,
                timestamp: Date.now(),
                redirectTo: `/home/${finalDealId}/${finalCountryId}`
            }));
            redirectUrl.searchParams.append("state", state);
        }

        // Also append the return URL
        const returnUrl = `${window.location.origin}/login`;
        redirectUrl.searchParams.append("return_url", returnUrl);

        console.log("Full redirect URL:", redirectUrl.toString());
        console.log("Return URL will be:", returnUrl);

        // Clear the navigation flag
        hasNavigatedRef.current = false;

        // Redirect to SSO
        window.location.href = redirectUrl.toString();
    };

    const fetchUserRole = async (email, token) => {
        try {
            const res = await getUserRole(email, token);
            if (res && res?.data?.status === 200) {
                console.log("User22222:", res?.data?.data?.roles[0]);
                if (res?.data?.data?.roles[0]) {
                    localStorage.setItem('userRole', res?.data?.data?.roles[0])
                }
            }
        } catch (error) {
            console.error("Error fetching user role:", error);
        }
    };

    return (
        <>
            {showLoader && <FullScreenLoader />}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnHover
                draggable
                theme="colored"
            />
            <div className="login-wrapper-body">
                <Container fluid>
                    <Row className='justify-content-center'>
                        <Col xs={12} md={5} lg={4}>
                            <div className="login-contact-wrapper login_left_panel corner-border">
                                <div className="container-style">
                                    <div className="d-flex justify-content-center">
                                        <img src={stageLogo} className="logo-brand-wth" alt="logo" />
                                    </div>

                                    <div className="wap-formcontact pt-4">
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <p style={{ color: 'white' }}>Asset management</p>
                                        </div>

                                        <Form>
                                            <Form.Group className="mb-3">
                                                <Form.Control type="email" placeholder="Enter email" className='input-item-fill' />
                                            </Form.Group>

                                            <Form.Group className="mb-3">
                                                <Form.Control type="password" placeholder="Enter Password" className='input-item-fill' />
                                            </Form.Group>

                                            <Form.Group>
                                                <button disabled className="btn w-100 btn-submit-web btn-submit-login">
                                                    Login
                                                </button>
                                            </Form.Group>

                                            <Form.Group className='my-3'>
                                                <div className="divider-container">
                                                    <div className="divider-line"></div>
                                                    <div className="divider-text">or</div>
                                                    <div className="divider-line"></div>
                                                </div>
                                            </Form.Group>

                                            <Form.Group>
                                                <button
                                                    onClick={handleSsoClick}
                                                    className="btn w-100 btn-submit-web btn-submit-login"
                                                >
                                                    <span className="form-label-text-btn">
                                                        <img src={ssoLogo} className="image-gap me-2" alt="sso" />
                                                        SSO
                                                    </span>
                                                </button>
                                            </Form.Group>

                                            {/* Debug info - show current parameters */}

                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
}

export default Login;